<script setup lang="ts">
import { computed } from "vue";
import { useTranslation } from "i18next-vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import DevInfoPopover from "@/components/canvas/analysis/DevInfoPopover.vue";
import { usePlanAnalysisState } from "@/stores/PlanAnalysisState";

/**
 * En-tête STATIQUE de l'onglet IA : la barre de progression de validation.
 * L'outillage (vues détectées, cases zones/cadres, exporter/effacer) vit dans
 * l'infobulle orange « Mode développeur », à droite de la progression — il
 * sortira de l'UX finale. Les onglets du volet sont dans
 * `ObservationsDrawerTabs`, la relance de l'analyse en pied de l'onglet.
 */

defineEmits<{
  (e: "export-fixture"): void;
  (e: "clear"): void;
}>();

const { t } = useTranslation();
const state = usePlanAnalysisState();

/** Export de fixture de calibration — outil de dev uniquement. */
const isDev = import.meta.env.DEV;

/** Libellé des vues détectées (titre de fond de plan, sinon id). */
const vignetteNames = computed(() =>
  state.vignettes.map((v) => v.name ?? v.id).join(", "),
);

const progressPercent = computed(() =>
  state.groups.length === 0
    ? 0
    : Math.round((state.validatedCount / state.groups.length) * 100),
);
</script>

<template>
  <div class="analysis-header">
    <!-- Progression de validation + infobulle dev -->
    <div class="d-flex align-items-center gap-2">
      <div class="flex-grow-1">
        <div class="progress-row d-flex justify-content-between">
          <span>
            {{
              t("canvas:analysis.progressValidated", {
                validated: state.validatedCount,
                total: state.groups.length,
              })
            }}
          </span>
          <span>
            {{ t("canvas:analysis.toValidate", { count: state.pendingCount }) }}
          </span>
        </div>
        <div class="progress-track mt-1">
          <div class="progress-fill" :style="{ width: `${progressPercent}%` }" />
        </div>
      </div>
      <DevInfoPopover :width="300">
        <div v-if="state.vignettes.length" class="dev-row">
          {{
            t("canvas:analysis.viewsDetected", {
              count: state.vignettes.length,
              names: vignetteNames,
            })
          }}
        </div>
        <div
          v-if="state.scanExcluded && (state.scanExcluded.stamped || state.scanExcluded.stale)"
          class="dev-row"
        >
          {{
            t("canvas:analysis.stampedExcluded", {
              stamped: state.scanExcluded.stamped,
              stale: state.scanExcluded.stale,
            })
          }}
        </div>
        <div
          v-if="
            state.unassignedTextHandles.length ||
            state.unassignedMarkerHandles.length
          "
          class="dev-row"
        >
          {{
            t("canvas:analysis.unassigned", {
              texts: state.unassignedTextHandles.length,
              markers: state.unassignedMarkerHandles.length,
            })
          }}
        </div>
        <div class="dev-row d-flex flex-column">
          <el-checkbox v-model="state.zonesVisible" size="small">
            {{ t("canvas:analysis.showZones") }}
          </el-checkbox>
          <el-checkbox v-model="state.framesVisible" size="small">
            {{ t("canvas:analysis.showFrames") }}
          </el-checkbox>
        </div>
        <div class="dev-row d-flex flex-column gap-1 mt-1">
          <DRBtn
            v-if="isDev"
            icon="bi-download"
            :label="t('canvas:analysis.exportFixture')"
            variant="outline"
            size="xs"
            @click="$emit('export-fixture')"
          />
          <DRBtn
            icon="bi-trash"
            :label="t('canvas:analysis.clear.button')"
            variant="outline-danger"
            size="xs"
            @click="$emit('clear')"
          />
        </div>
      </DevInfoPopover>
    </div>

    <div v-if="state.isAiRunning" class="ai-running mt-2">
      <span class="spinner-border spinner-border-sm" role="status" />
      {{ t("canvas:analysis.aiRunning") }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.analysis-header {
  // Partie statique : reste visible pendant que la liste défile dessous.
  position: sticky;
  top: 0;
  z-index: 3;
  background-color: var(--canvas-bg);
  border-bottom: 1px solid var(--canvas-border);
  padding-bottom: 10px;
}

.progress-row {
  font-size: 0.74rem;
  opacity: 0.85;
}

.progress-track {
  height: 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.1);
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 2px;
  background-color: var(--bs-success);
  transition: width 0.25s ease;
}

.dev-row {
  padding: 2px 0;
}

.ai-running {
  font-size: 0.74rem;
  color: var(--dr-report-light);
  display: flex;
  align-items: center;
  gap: 6px;

  .spinner-border {
    width: 12px;
    height: 12px;
    border-width: 2px;
  }
}
</style>
